import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AUTH_TOKEN_KEY,
  ApiRequestError,
  canEditTeamInfo,
  fetchJoinedTeamMembersPage,
  fetchPendingTeamRequestsPage,
  fetchSystemUserInfo,
  fetchTeamSubPrice,
  getTeamAdminId,
  isServicerTeamAdmin,
  postHandleTeamRequest,
  postUpdateServicerHourlyWage,
  parseDisplayTeamInfo,
  readTeamCountry,
  showApiResultModal,
  formatTeamMemberHourlyWage,
  type DisplayTeamInfo,
  type HandleTeamRequestAction,
  type PendingTeamRequest,
  type TeamMember,
  type TeamSubPrice,
} from '@/api';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { InlineFieldError } from '@/components/ui/FormFeedback';
import { CurrencyAmountInput } from '@/components/ui/CurrencyAmountInput';
import EditHourlyWageModal, { type HourlyWageEditTarget } from '@/pages/orders/components/EditHourlyWageModal';
import { hasServicerHourlyWage } from '@/api/modules/order';
import {
  DEFAULT_TEAM_COUNTRY,
  normalizeTeamCountry,
  teamCountryCurrency,
  type TeamCountryCode,
} from '@/constants/teamCountries';
import { formatServiceAreaLabel } from '@/constants/serviceAreas';

const PAGE_SIZE = 10;

/** Backend request_type: 2 = apply to leave team */
const EXIT_REQUEST_TYPE = 2;

type MemberTab = 'joined' | 'pending' | 'exit';

const HOURLY_WAGE_MISSING_MESSAGE = 'Hourly not set';

type MemberActionTarget =
  | { kind: 'pending'; request: PendingTeamRequest }
  | { kind: 'exit'; request: PendingTeamRequest };

function isExitRequest(request: PendingTeamRequest): boolean {
  return request.requestType === EXIT_REQUEST_TYPE;
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback below
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

function resolveSeatQuota(price: TeamSubPrice | null, joinedCount: number) {
  const cap = price?.teamCapacity;
  const limit = cap?.totalCapacity ?? price?.baseMemberCount ?? 0;
  const used = cap?.currentCount ?? joinedCount;
  const remaining = cap ? Math.max(0, cap.remaining) : Math.max(0, limit - used);
  return {
    limit,
    used,
    remaining,
    atLimit: limit > 0 && remaining <= 0,
  };
}

function formatMemberServiceAreas(codes: string[] | undefined): string {
  if (!codes || codes.length === 0) return 'No service areas';
  const labels = codes.map((code) => formatServiceAreaLabel(code));
  if (labels.length <= 3) return labels.join(', ');
  return `${labels.slice(0, 3).join(', ')} +${labels.length - 3}`;
}

export default function TeamPage() {
  const [activeTab, setActiveTab] = useState<MemberTab>('joined');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCopying, setInviteCopying] = useState(false);
  const [teamInfo, setTeamInfo] = useState<DisplayTeamInfo | null>(null);
  const [teamCountry, setTeamCountry] = useState<TeamCountryCode>(DEFAULT_TEAM_COUNTRY);
  const [teamInfoLoading, setTeamInfoLoading] = useState(true);
  const [canEditTeam, setCanEditTeam] = useState(false);
  const [teamAdminId, setTeamAdminId] = useState<string | null>(null);

  const [joinedMembers, setJoinedMembers] = useState<TeamMember[]>([]);
  const [joinedTotal, setJoinedTotal] = useState(0);
  const [joinedPage, setJoinedPage] = useState(1);
  const [joinedLoading, setJoinedLoading] = useState(true);
  const [joinedLoadingMore, setJoinedLoadingMore] = useState(false);

  const [pendingRequests, setPendingRequests] = useState<PendingTeamRequest[]>([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingLoadingMore, setPendingLoadingMore] = useState(false);

  const [listError, setListError] = useState<string | null>(null);
  const [subPrice, setSubPrice] = useState<TeamSubPrice | null>(null);
  const [showSeatLimitModal, setShowSeatLimitModal] = useState(false);

  const [reviewRequest, setReviewRequest] = useState<PendingTeamRequest | null>(null);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const [actionTarget, setActionTarget] = useState<MemberActionTarget | null>(null);
  const [joinWageInput, setJoinWageInput] = useState('');
  const [joinWageError, setJoinWageError] = useState('');
  const [rejectReasonInput, setRejectReasonInput] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState('');
  const [wageEditTarget, setWageEditTarget] = useState<HourlyWageEditTarget | null>(null);
  const [wageSubmitting, setWageSubmitting] = useState(false);

  const navigate = useNavigate();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const joinedPageRef = useRef(1);
  const pendingPageRef = useRef(1);
  const joinedLoadingMoreRef = useRef(false);
  const pendingLoadingMoreRef = useRef(false);

  const joinRequests = useMemo(
    () => pendingRequests.filter((r) => !isExitRequest(r)),
    [pendingRequests],
  );
  const exitRequests = useMemo(
    () => pendingRequests.filter(isExitRequest),
    [pendingRequests],
  );

  useEffect(() => {
    joinedPageRef.current = joinedPage;
  }, [joinedPage]);

  useEffect(() => {
    pendingPageRef.current = pendingPage;
  }, [pendingPage]);

  const loadJoinedFirstPage = useCallback(async () => {
    setJoinedLoading(true);
    setListError(null);
    setJoinedPage(1);
    joinedPageRef.current = 1;
    try {
      const { items, total } = await fetchJoinedTeamMembersPage({ page: 1, pageSize: PAGE_SIZE });
      setJoinedMembers(items);
      setJoinedTotal(total);
    } catch (err) {
      setJoinedMembers([]);
      setJoinedTotal(0);
      setListError(err instanceof Error ? err.message : 'Failed to load team members.');
    } finally {
      setJoinedLoading(false);
    }
  }, []);

  const loadSeatQuota = useCallback(async (country?: string) => {
    try {
      const price = await fetchTeamSubPrice({
        country: country ?? DEFAULT_TEAM_COUNTRY,
      });
      setSubPrice(price);
    } catch {
      setSubPrice(null);
    }
  }, []);

  const loadPendingFirstPage = useCallback(async () => {
    setPendingLoading(true);
    setListError(null);
    setPendingPage(1);
    pendingPageRef.current = 1;
    try {
      const { items, total } = await fetchPendingTeamRequestsPage({ page: 1, pageSize: PAGE_SIZE });
      setPendingRequests(items);
      setPendingTotal(total);
    } catch (err) {
      setPendingRequests([]);
      setPendingTotal(0);
      setListError(err instanceof Error ? err.message : 'Failed to load pending requests.');
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      setTeamInfo(null);
      setCanEditTeam(false);
      setTeamInfoLoading(false);
      setJoinedLoading(false);
      setPendingLoading(false);
      return;
    }

    let cancelled = false;
    fetchSystemUserInfo()
      .then((profile) => {
        if (cancelled) return;
        if (!canEditTeamInfo(profile)) {
          navigate('/team', { replace: true });
          return;
        }
        setTeamInfo(parseDisplayTeamInfo(profile.team_info));
        const country = normalizeTeamCountry(readTeamCountry(profile.team_info));
        setTeamCountry(country);
        setCanEditTeam(canEditTeamInfo(profile));
        setTeamAdminId(getTeamAdminId(profile));
        return Promise.all([loadJoinedFirstPage(), loadPendingFirstPage(), loadSeatQuota(country)]);
      })
      .catch(() => {
        if (!cancelled) {
          setTeamInfo(null);
          setCanEditTeam(false);
        }
      })
      .finally(() => {
        if (!cancelled) setTeamInfoLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, loadJoinedFirstPage, loadPendingFirstPage, loadSeatQuota]);

  const loadMoreJoined = useCallback(async () => {
    if (joinedLoadingMoreRef.current || joinedLoading || joinedLoadingMore) return;
    if (joinedTotal === 0 || joinedMembers.length >= joinedTotal) return;

    joinedLoadingMoreRef.current = true;
    setJoinedLoadingMore(true);
    const nextPage = joinedPageRef.current + 1;
    try {
      const { items, total } = await fetchJoinedTeamMembersPage({
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      if (items.length === 0) {
        setJoinedTotal((prev) => Math.min(prev, joinedMembers.length));
        return;
      }
      setJoinedTotal(total);
      setJoinedPage(nextPage);
      joinedPageRef.current = nextPage;
      setJoinedMembers((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const next = [...prev];
        for (const row of items) {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            next.push(row);
          }
        }
        return next;
      });
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load more members.');
    } finally {
      joinedLoadingMoreRef.current = false;
      setJoinedLoadingMore(false);
    }
  }, [joinedLoading, joinedLoadingMore, joinedMembers.length, joinedTotal]);

  const loadMorePending = useCallback(async () => {
    if (pendingLoadingMoreRef.current || pendingLoading || pendingLoadingMore) return;
    if (pendingTotal === 0 || pendingRequests.length >= pendingTotal) return;

    pendingLoadingMoreRef.current = true;
    setPendingLoadingMore(true);
    const nextPage = pendingPageRef.current + 1;
    try {
      const { items, total } = await fetchPendingTeamRequestsPage({
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      if (items.length === 0) {
        setPendingTotal((prev) => Math.min(prev, pendingRequests.length));
        return;
      }
      setPendingTotal(total);
      setPendingPage(nextPage);
      pendingPageRef.current = nextPage;
      setPendingRequests((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        const next = [...prev];
        for (const row of items) {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            next.push(row);
          }
        }
        return next;
      });
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load more requests.');
    } finally {
      pendingLoadingMoreRef.current = false;
      setPendingLoadingMore(false);
    }
  }, [pendingLoading, pendingLoadingMore, pendingRequests.length, pendingTotal]);

  const loadMore = activeTab === 'joined' ? loadMoreJoined : loadMorePending;
  const listLoading = activeTab === 'joined' ? joinedLoading : pendingLoading;
  const loadingMore = activeTab === 'joined' ? joinedLoadingMore : pendingLoadingMore;
  const hasMore =
    activeTab === 'joined'
      ? joinedMembers.length < joinedTotal
      : pendingRequests.length < pendingTotal;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        void loadMore();
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const tabs: { key: MemberTab; label: string; count: number }[] = [
    { key: 'joined', label: 'Joined', count: joinedTotal },
    { key: 'pending', label: 'Pending', count: joinRequests.length },
    { key: 'exit', label: 'Exit', count: exitRequests.length },
  ];

  const pageLoading = teamInfoLoading;
  const seatQuota = resolveSeatQuota(subPrice, joinedTotal);
  const teamCurrency = teamCountryCurrency(teamCountry);

  const goAddSeats = () => {
    setShowSeatLimitModal(false);
    setShowInviteModal(false);
    navigate('/subscribe', { state: { mode: 'add_seats' } });
  };

  const openInvite = () => {
    if (!teamInfo) return;
    if (seatQuota.atLimit) {
      setShowSeatLimitModal(true);
      return;
    }
    setShowInviteModal(true);
  };

  const closeInviteModal = () => {
    if (inviteCopying) return;
    setShowInviteModal(false);
  };

  const confirmCopyInviteCode = async () => {
    const code = teamInfo?.teamCode?.trim();
    if (!code || inviteCopying) return;
    setInviteCopying(true);
    const ok = await copyTextToClipboard(code);
    setInviteCopying(false);
    setShowInviteModal(false);
    showApiResultModal({
      message: ok ? 'Invite code copied. Share it with your team members.' : 'Failed to copy invite code.',
      success: ok,
    });
  };

  const closeReviewModal = () => {
    setReviewRequest(null);
    setReviewError('');
    setReviewSuccess('');
  };

  const closeActionSheet = () => {
    if (reviewSubmitting) return;
    setActionTarget(null);
    setJoinWageError('');
    setRejectReasonError('');
  };

  const openPendingActions = (request: PendingTeamRequest) => {
    const wage =
      typeof request.hourlyWage === 'number' && request.hourlyWage > 0
        ? String(request.hourlyWage)
        : '';
    setJoinWageInput(wage);
    setJoinWageError('');
    setRejectReasonInput('');
    setRejectReasonError('');
    setActionTarget({ kind: 'pending', request });
    setReviewRequest(request);
    setReviewError('');
    setReviewSuccess('');
  };

  const openExitActions = (request: PendingTeamRequest) => {
    setActionTarget({ kind: 'exit', request });
    setReviewRequest(request);
    setReviewError('');
    setReviewSuccess('');
  };

  const submitHandleRequest = async (
    action: HandleTeamRequestAction,
    hourlyWage?: number,
    rejectReason?: string,
  ) => {
    if (!reviewRequest || reviewSubmitting) return;
    const approvingJoin = action === 1 && !isExitRequest(reviewRequest);
    if (approvingJoin && seatQuota.atLimit) {
      setShowSeatLimitModal(true);
      return;
    }

    setReviewSubmitting(true);
    setReviewError('');
    setReviewSuccess('');
    try {
      const result = await postHandleTeamRequest(
        reviewRequest.id,
        action,
        hourlyWage,
        rejectReason,
      );
      if (result.error) {
        setReviewError(result.message);
        showApiResultModal({ message: result.message, success: false });
        return;
      }

      const shouldRefreshMembers = action === 1 || isExitRequest(reviewRequest);
      closeReviewModal();
      setActionTarget(null);
      setJoinWageError('');
      setRejectReasonInput('');
      setRejectReasonError('');
      showApiResultModal({ message: result.message, success: true });
      await Promise.all([
        loadPendingFirstPage(),
        shouldRefreshMembers ? loadJoinedFirstPage() : Promise.resolve(),
        shouldRefreshMembers ? loadSeatQuota(teamCountry) : Promise.resolve(),
      ]);
    } catch (e) {
      const message =
        e instanceof ApiRequestError ? e.message : 'Failed to handle request.';
      setReviewError(message);
      showApiResultModal({ message, success: false });
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleReviewAction = async (action: HandleTeamRequestAction) => {
    await submitHandleRequest(action);
  };

  const handleRejectJoin = async () => {
    if (!reviewRequest || reviewSubmitting) return;
    const reason = rejectReasonInput.trim();
    if (!reason) {
      setRejectReasonError('Please enter a reject reason.');
      return;
    }
    setRejectReasonError('');
    await submitHandleRequest(2, undefined, reason);
  };

  const handleApproveJoin = async () => {
    if (!reviewRequest || reviewSubmitting) return;
    if (seatQuota.atLimit) {
      setShowSeatLimitModal(true);
      return;
    }
    const parsed = Number(joinWageInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setJoinWageError('Please enter an hourly wage greater than 0.');
      return;
    }

    const hourlyWage = Math.round(parsed * 100) / 100;
    setJoinWageError('');
    await submitHandleRequest(1, hourlyWage);
  };

  const isTeamCreator = (operatorId: string | number | null | undefined) =>
    isServicerTeamAdmin(operatorId, teamAdminId);

  const openWageModalForMember = (member: TeamMember) => {
    if (!canEditTeam) return;
    setWageEditTarget({
      operatorId: String(member.operatorId),
      name: member.name,
      hourlyWage: member.hourlyWage,
      country: teamCountry,
      hint: isTeamCreator(member.operatorId) ? 'Team admin hourly wage is optional.' : undefined,
    });
  };

  const closeWageModal = () => {
    if (wageSubmitting) return;
    setWageEditTarget(null);
  };

  const handleConfirmHourlyWage = async (hourlyWage: number) => {
    if (!wageEditTarget || wageSubmitting) return;

    setWageSubmitting(true);
    try {
      const result = await postUpdateServicerHourlyWage({
        operatorId: wageEditTarget.operatorId,
        hourlyWage,
      });
      setJoinedMembers((prev) =>
        prev.map((member) =>
          String(member.operatorId) === String(wageEditTarget.operatorId)
            ? { ...member, hourlyWage: result.hourlyWage }
            : member,
        ),
      );
      setPendingRequests((prev) =>
        prev.map((request) =>
          String(request.operatorId) === String(wageEditTarget.operatorId)
            ? { ...request, hourlyWage: result.hourlyWage }
            : request,
        ),
      );
      setReviewRequest((prev) =>
        prev && String(prev.operatorId) === String(wageEditTarget.operatorId)
          ? { ...prev, hourlyWage: result.hourlyWage }
          : prev,
      );
      setActionTarget((prev) => {
        if (!prev) return prev;
        if (String(prev.request.operatorId) === String(wageEditTarget.operatorId)) {
          return { ...prev, request: { ...prev.request, hourlyWage: result.hourlyWage } };
        }
        return prev;
      });
      setWageEditTarget(null);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to update hourly wage.',
        success: false,
      });
    } finally {
      setWageSubmitting(false);
    }
  };

  const renderRequestRow = (request: PendingTeamRequest, variant: 'pending' | 'exit') => {
    const isExit = variant === 'exit';
    const hasHourlyWage = hasServicerHourlyWage({ hourlyWage: request.hourlyWage });
    return (
      <button
        key={request.id}
        type="button"
        onClick={() => (isExit ? openExitActions(request) : openPendingActions(request))}
        className="w-full p-3 text-left cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div
            className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 border ${
              isExit ? 'bg-red-50 border-red-100' : 'bg-orange-50 border-orange-100'
            }`}
          >
            <i
              className={`text-lg ${
                isExit ? 'ri-logout-box-r-line text-red-500' : 'ri-user-received-line text-orange-500'
              }`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-900 font-semibold text-sm truncate">{request.applicant}</span>
              <span
                className={`text-[9px] font-medium px-1 py-0.5 rounded border flex-shrink-0 ${
                  isExit
                    ? 'bg-red-50 text-red-600 border-red-200'
                    : 'bg-orange-50 text-orange-600 border-orange-200'
                }`}
              >
                {isExit ? 'Exit' : 'Pending'}
              </span>
            </div>
            {!isExit && (
              <p className={`text-[11px] mt-0.5 ${hasHourlyWage ? 'text-gray-500' : 'text-red-500'}`}>
                {hasHourlyWage
                  ? formatTeamMemberHourlyWage(request.hourlyWage)
                  : HOURLY_WAGE_MISSING_MESSAGE}
              </p>
            )}
            {request.createTime && (
              <p className="text-gray-400 text-[11px] mt-0.5">{request.createTime}</p>
            )}
          </div>
          <i className="ri-arrow-right-s-line text-xl text-gray-400 flex-shrink-0" />
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] pb-6">
      <SubpageHeader
        title="Member Management"
        onBack={() => navigate('/me')}
      />

      <div className="px-4 pt-3 space-y-2.5">
        {pageLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
            <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
            <p className="text-gray-400 text-xs mt-2">Loading team info…</p>
          </div>
        ) : !teamInfo ? (
          <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
            <div className="w-12 h-12 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center mx-auto mb-2.5">
              <i className="ri-team-line text-green-500 text-xl" />
            </div>
            <h2 className="text-gray-900 font-semibold text-sm mb-1">No Team Yet</h2>
            <p className="text-gray-400 text-xs leading-relaxed mb-3">
              Join an existing team with an invite code from your admin.
            </p>
            <button
              type="button"
              onClick={() => navigate('/me/team/join')}
              className="w-full h-10 rounded-lg border border-green-500 text-green-600 font-normal text-sm cursor-pointer hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-login-circle-line" />
              Join Team
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
            <div className="flex items-center gap-3">
              {teamInfo.logoPath ? (
                <img
                  src={teamInfo.logoPath}
                  alt={teamInfo.name}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-gray-100"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-green-700 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm select-none">
                    {teamInfo.name
                      .split(' ')
                      .slice(0, 2)
                      .map((w) => w[0])
                      .join('')}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-gray-900 font-semibold text-sm truncate">{teamInfo.name}</div>
                {teamInfo.teamCode ? (
                  <div className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-500 min-w-0">
                    <span className="font-medium flex-shrink-0">Team Code</span>
                    <span className="text-sky-500">
                      {teamInfo.teamCode.toUpperCase()}
                    </span>
                  </div>
                ) : teamInfo.tagline ? (
                  <p className="text-gray-400 text-xs mt-0.5 truncate">{teamInfo.tagline}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={openInvite}
                className="flex items-center gap-0.5 text-xs font-normal text-green-600 hover:text-green-700 cursor-pointer flex-shrink-0"
              >
                Invite
                <i className="ri-arrow-right-s-line text-base leading-none" />
              </button>
            </div>

            {seatQuota.limit > 0 && (
              <div
                className={`rounded-md border px-2.5 py-2 flex items-center gap-2 ${
                  seatQuota.atLimit ? 'bg-amber-50/70 border-amber-200' : 'bg-gray-50 border-gray-100'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-gray-800 text-xs font-semibold">
                    {seatQuota.used}/{seatQuota.limit} seats
                    {seatQuota.atLimit ? ' — limit reached' : ` · ${seatQuota.remaining} left`}
                  </p>
                  <p className="text-gray-400 text-[11px] mt-0.5 leading-snug">
                    {seatQuota.atLimit
                      ? 'Upgrade your plan to invite or approve more members.'
                      : 'Invites and join requests use remaining seats.'}
                  </p>
                </div>
                {seatQuota.atLimit && (
                  <button
                    type="button"
                    onClick={goAddSeats}
                    className="flex-shrink-0 h-8 px-2.5 text-xs font-normal text-amber-800 bg-white border border-amber-200 rounded-md cursor-pointer hover:bg-amber-50 transition-colors"
                  >
                    Change plan
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {listError && (
          <div className="rounded-lg border border-red-200 bg-red-50/50 px-3 py-2">
            <p className="text-red-600 text-xs">{listError}</p>
          </div>
        )}

        {teamInfo && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex border-b border-gray-200 px-1">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 py-2.5 flex items-center justify-center gap-1 text-xs font-semibold cursor-pointer whitespace-nowrap border-b-2 -mb-px transition-colors ${
                    activeTab === tab.key
                      ? 'text-green-600 border-green-600'
                      : 'text-gray-400 border-transparent'
                  }`}
                >
                  {tab.label}
                  {(tab.key === 'joined' || tab.count > 0) && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold leading-none ${
                        tab.key === 'pending'
                          ? 'bg-orange-50 text-orange-600'
                          : tab.key === 'exit'
                            ? 'bg-red-50 text-red-600'
                            : activeTab === tab.key
                              ? 'bg-green-50 text-green-600'
                              : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="divide-y divide-gray-100">
              {activeTab === 'joined' && (
                <>
                  {listLoading && (
                    <div className="p-6 text-center">
                      <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
                      <p className="text-gray-400 text-xs mt-2">Loading members…</p>
                    </div>
                  )}
                  {!listLoading && joinedMembers.length === 0 && !listError && (
                    <div className="p-6 text-center">
                      <i className="ri-group-line text-gray-300 text-2xl" />
                      <p className="text-gray-400 text-xs mt-2">No joined members yet.</p>
                    </div>
                  )}
                  {!listLoading &&
                    joinedMembers.map((member) => {
                      const hasHourlyWage = hasServicerHourlyWage({ hourlyWage: member.hourlyWage });
                      const isAdmin = isTeamCreator(member.operatorId);
                      const showWageMissing = !isAdmin && !hasHourlyWage;
                      return (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => openWageModalForMember(member)}
                          disabled={!canEditTeam}
                          className="w-full p-3 text-left cursor-pointer hover:bg-gray-50 transition-colors disabled:cursor-default disabled:hover:bg-transparent"
                        >
                          <div className="flex items-start gap-2.5">
                            <div className="flex flex-col items-center flex-shrink-0 w-11">
                              <div className="relative w-11 h-11">
                                {member.avatar ? (
                                  <img
                                    src={member.avatar}
                                    alt={member.name}
                                    className="w-11 h-11 rounded-full object-cover object-top border border-gray-100"
                                  />
                                ) : (
                                  <div className="w-11 h-11 rounded-full bg-green-50 border border-green-100 flex items-center justify-center">
                                    <span className="text-green-700 text-xs font-semibold">
                                      {member.name
                                        .split(' ')
                                        .slice(0, 2)
                                        .map((w) => w[0])
                                        .join('')
                                        .toUpperCase() || '?'}
                                    </span>
                                  </div>
                                )}
                                <span
                                  className={`absolute top-0 right-0 w-[0.9rem] h-[0.9rem] rounded-full border-2 border-white ${
                                    member.isOnline ? 'bg-green-500' : 'bg-gray-300'
                                  }`}
                                  aria-label={member.isOnline ? 'Online' : 'Offline'}
                                />
                              </div>
                              <span
                                className={`mt-0.5 text-[9px] leading-none font-medium ${
                                  member.isOnline ? 'text-green-600' : 'text-gray-400'
                                }`}
                              >
                                {member.isOnline ? 'Online' : 'Offline'}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-gray-900 font-semibold text-sm truncate">
                                  {member.name}
                                </span>
                                {isAdmin && (
                                  <span className="text-[9px] font-medium px-1 py-0.5 rounded border flex-shrink-0 bg-green-50 text-green-700 border-green-200">
                                    Admin
                                  </span>
                                )}
                              </div>
                              {isAdmin ? (
                                hasHourlyWage ? (
                                  <p className="text-gray-500 text-[11px] mt-0.5">
                                    {formatTeamMemberHourlyWage(member.hourlyWage)}
                                  </p>
                                ) : null
                              ) : showWageMissing ? (
                                <p className="text-red-500 text-[11px] mt-0.5">{HOURLY_WAGE_MISSING_MESSAGE}</p>
                              ) : (
                                <p className="text-gray-500 text-[11px] mt-0.5">
                                  {formatTeamMemberHourlyWage(member.hourlyWage)}
                                </p>
                              )}
                              <p className="text-gray-500 text-[11px] mt-0.5 flex items-start gap-1 min-w-0">
                                <i className="ri-phone-line text-gray-400 text-xs mt-px flex-shrink-0" />
                                <span className="truncate">{member.phone.trim() || 'No phone'}</span>
                              </p>
                              <p className="text-gray-500 text-[11px] mt-0.5 flex items-start gap-1 min-w-0">
                                <i className="ri-map-pin-line text-gray-400 text-xs mt-px flex-shrink-0" />
                                <span className="line-clamp-2">
                                  {formatMemberServiceAreas(member.postalCodes)}
                                </span>
                              </p>
                              {member.joinDate && (
                                <p className="text-gray-400 text-[11px] mt-0.5">
                                  Joined {new Date(member.joinDate).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <i className="ri-arrow-right-s-line text-xl text-gray-400 flex-shrink-0 mt-3" />
                          </div>
                        </button>
                      );
                    })}
                </>
              )}

              {activeTab === 'pending' && (
                <>
                  {listLoading && (
                    <div className="p-6 text-center">
                      <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
                      <p className="text-gray-400 text-xs mt-2">Loading requests…</p>
                    </div>
                  )}
                  {!listLoading && joinRequests.length === 0 && !listError && (
                    <div className="p-6 text-center">
                      <i className="ri-time-line text-gray-300 text-2xl" />
                      <p className="text-gray-400 text-xs mt-2">No pending join requests.</p>
                    </div>
                  )}
                  {!listLoading && joinRequests.map((request) => renderRequestRow(request, 'pending'))}
                </>
              )}

              {activeTab === 'exit' && (
                <>
                  {listLoading && (
                    <div className="p-6 text-center">
                      <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
                      <p className="text-gray-400 text-xs mt-2">Loading requests…</p>
                    </div>
                  )}
                  {!listLoading && exitRequests.length === 0 && !listError && (
                    <div className="p-6 text-center">
                      <i className="ri-logout-box-r-line text-gray-300 text-2xl" />
                      <p className="text-gray-400 text-xs mt-2">No exit requests.</p>
                    </div>
                  )}
                  {!listLoading && exitRequests.map((request) => renderRequestRow(request, 'exit'))}
                </>
              )}
            </div>

            {hasMore && (
              <div ref={sentinelRef} className="py-2 text-center border-t border-gray-100">
                {loadingMore && (
                  <>
                    <i className="ri-loader-4-line animate-spin text-green-500 text-xl" />
                    <p className="text-gray-400 text-xs mt-1.5">Loading more…</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={closeInviteModal}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-sm bg-white rounded-lg border border-gray-200 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-gray-900 font-bold text-base">Invite Members</h3>
                <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                  Copy the invite code and send it to team members so they can request to join.
                </p>
              </div>
              <button
                type="button"
                onClick={closeInviteModal}
                disabled={inviteCopying}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors flex-shrink-0 disabled:opacity-50"
                aria-label="Close"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            {teamInfo.teamCode && (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 mb-4 text-center">
                <div className="text-gray-400 text-[11px] mb-1">Team Invite Code</div>
                <div className="text-sky-600 font-mono font-semibold text-lg tracking-wider">
                  {teamInfo.teamCode.toUpperCase()}
                </div>
              </div>
            )}

            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={closeInviteModal}
                disabled={inviteCopying}
                className="flex-1 h-9 rounded-lg border border-gray-200 text-gray-600 font-medium text-xs cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmCopyInviteCode()}
                disabled={inviteCopying || !teamInfo.teamCode}
                className="flex-1 h-9 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium text-xs cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                {inviteCopying ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <>
                    <i className="ri-file-copy-line" />
                    Copy Code
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {actionTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={closeActionSheet}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
          <div
            className="relative w-full max-w-md mx-auto bg-white rounded-lg border border-gray-200 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-900 font-semibold text-sm">
                {actionTarget.kind === 'exit' ? 'Exit Request' : 'Join Request'}
              </h3>
              <button
                type="button"
                onClick={closeActionSheet}
                disabled={reviewSubmitting}
                className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <i className="ri-close-line text-gray-500 text-base" />
              </button>
            </div>

            {actionTarget.kind === 'pending' && reviewRequest && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleApproveJoin();
                }}
              >
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mb-3">
                  <div className="text-gray-900 font-semibold text-sm truncate">
                    {reviewRequest.applicant}
                  </div>
                  <div className="text-gray-500 text-xs mt-0.5">{reviewRequest.requestTypeText}</div>
                </div>

                <label className="block text-xs font-normal text-gray-600 mb-1.5">
                  Hourly Wage ({teamCurrency.code}) <span className="text-red-500">*</span>
                </label>
                <CurrencyAmountInput
                  value={joinWageInput}
                  onChange={(value) => {
                    setJoinWageInput(value);
                    setJoinWageError('');
                  }}
                  currencySymbol={teamCurrency.symbol}
                  disabled={reviewSubmitting || Boolean(reviewSuccess)}
                  aria-invalid={Boolean(joinWageError)}
                  className="mb-1"
                />
                <InlineFieldError message={joinWageError} />

                <p className="text-gray-500 text-[11px] leading-relaxed mt-2 mb-3">
                  Enter the hourly wage and approve in one step. Members without a wage cannot join.
                  {seatQuota.atLimit
                    ? ' Seat capacity is full — reject this request, or change your plan before approving.'
                    : ''}
                </p>

                <label className="block text-xs font-normal text-gray-600 mb-1.5">
                  Reject reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectReasonInput}
                  onChange={(e) => {
                    setRejectReasonInput(e.target.value);
                    setRejectReasonError('');
                  }}
                  maxLength={200}
                  rows={3}
                  disabled={reviewSubmitting || Boolean(reviewSuccess)}
                  placeholder="Required when rejecting this join request"
                  aria-invalid={Boolean(rejectReasonError)}
                  className={`w-full px-2.5 py-2 rounded-md border text-xs font-normal outline-none resize-none mb-1 disabled:bg-gray-50 disabled:text-gray-400 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                    rejectReasonError
                      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
                      : 'border-gray-200 focus:border-green-500'
                  }`}
                />
                <div className="flex items-center justify-between mb-3">
                  <InlineFieldError message={rejectReasonError} />
                  <span className="text-gray-400 text-[10px] ml-auto">
                    {rejectReasonInput.trim().length}/200
                  </span>
                </div>

                {reviewError && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-2.5 py-2 mb-2.5">
                    {reviewError}
                  </div>
                )}
                {reviewSuccess && (
                  <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-md px-2.5 py-2 mb-2.5">
                    {reviewSuccess}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => void handleRejectJoin()}
                    disabled={reviewSubmitting || Boolean(reviewSuccess)}
                    className="ui-btn-sm w-full border border-red-200 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50"
                  >
                    {reviewSubmitting ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <>
                        <i className="ri-close-circle-line" />
                        Reject
                      </>
                    )}
                  </button>
                  <button
                    type="submit"
                    disabled={reviewSubmitting || Boolean(reviewSuccess)}
                    className="ui-btn-sm w-full bg-green-500 hover:bg-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reviewSubmitting ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <>
                        <i className="ri-check-line" />
                        Approve
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {actionTarget.kind === 'exit' && reviewRequest && (
              <>
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mb-3">
                  <div className="text-gray-900 font-semibold text-sm truncate">
                    {reviewRequest.applicant}
                  </div>
                  <div className="text-gray-500 text-xs mt-0.5">{reviewRequest.requestTypeText}</div>
                  {reviewRequest.createTime && (
                    <div className="text-gray-400 text-[11px] mt-0.5">{reviewRequest.createTime}</div>
                  )}
                </div>

                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-2 mb-3 leading-relaxed">
                  Approving exit removes this member from the team. They will no longer receive team
                  orders and must rejoin with a team code if they want to come back.
                </div>

                {reviewError && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-2.5 py-2 mb-2.5">
                    {reviewError}
                  </div>
                )}
                {reviewSuccess && (
                  <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-md px-2.5 py-2 mb-2.5">
                    {reviewSuccess}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => void handleReviewAction(2)}
                    disabled={reviewSubmitting || Boolean(reviewSuccess)}
                    className="ui-btn-sm w-full border border-gray-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {reviewSubmitting ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      'Keep in Team'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleReviewAction(1)}
                    disabled={reviewSubmitting || Boolean(reviewSuccess)}
                    className="ui-btn-sm w-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reviewSubmitting ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <>
                        <i className="ri-logout-box-r-line" />
                        Approve Exit
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showSeatLimitModal && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={() => setShowSeatLimitModal(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-2xl px-4 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
            style={{ maxWidth: 480, margin: '0 auto' }}
          >
            <div className="w-8 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-amber-50 border border-amber-100 mb-2.5">
                <i className="ri-vip-diamond-line text-amber-500 text-xl" />
              </div>
              <div className="text-gray-900 font-bold text-base">Seat limit reached</div>
              <p className="text-gray-400 text-xs mt-1.5 leading-relaxed px-1">
                Your team is using {seatQuota.used}/{seatQuota.limit} seats. Change your subscription
                plan to add seats before inviting or approving new members.
              </p>
            </div>
            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={() => setShowSeatLimitModal(false)}
                className="flex-1 h-11 rounded-lg border border-gray-200 text-gray-600 font-normal text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={goAddSeats}
                className="flex-1 h-11 rounded-lg bg-green-500 hover:bg-green-600 text-white font-normal text-sm cursor-pointer whitespace-nowrap transition-colors"
              >
                Change plan
              </button>
            </div>
          </div>
        </>
      )}

      <EditHourlyWageModal
        target={wageEditTarget}
        submitting={wageSubmitting}
        onClose={closeWageModal}
        onConfirm={(hourlyWage) => void handleConfirmHourlyWage(hourlyWage)}
      />
    </div>
  );
}
